import type { BreadcrumbLink, BreadcrumbRoute } from '@monecolier/ui';
import { NAV } from './lib/navigation';

/**
 * UX-85 — fil d'Ariane de web-admin : pattern de route → maillons parents.
 * Mécanisme et choix d'architecture : voir packages/ui/src/Breadcrumbs.tsx.
 *
 * Les parents sont des entrées de la Sidebar (`NAV`, lib/navigation.ts) :
 * jamais un libellé recopié. Seul le libellé de la page COURANTE est écrit
 * ici quand la page n'est pas elle-même une entrée de menu ; pour une route à
 * segment dynamique, c'est l'état transitoire affiché avant que la page ne
 * pousse la donnée réelle via `useBreadcrumb`.
 *
 * N'y figurent que les pages SOUS une entrée de menu : une entrée de menu de
 * premier niveau n'aurait qu'un maillon (non affiché), et les pages
 * d'authentification/d'accueil (`/`, `/login`, `/register`,
 * `/forgot-password`, `/reset-password`) sont exclues par construction.
 * Les redirections (`/reenrollments`, `/supply-catalog`…) n'ont pas besoin
 * d'entrée : le fil est calculé sur l'URL de destination.
 */
function link(item: { to: string; label: string }, keepSearchParams?: string[]): BreadcrumbLink {
  return { to: item.to, label: item.label, keepSearchParams };
}

// DSH-04 : drill-down Dashboard financier → Par classe → classe → élève.
// `schoolYearId` fait partie de l'identité de la vue (quelle année est
// agrégée) : il suit le lien « Par classe », comme le faisait le fil local
// que ces pages portaient avant UX-85.
const FINANCIAL_DRILLDOWN: BreadcrumbLink[] = [
  link(NAV.financialDashboard),
  link(NAV.financialByClass, ['schoolYearId']),
];

export const breadcrumbRoutes: BreadcrumbRoute[] = [
  // Élèves & Inscriptions
  {
    path: '/students/:studentId',
    parents: [link(NAV.students)],
    label: 'Fiche élève',
    // DSH-04 : la fiche est aussi le dernier niveau du drill-down financier.
    alternatives: [{ from: '/financial-by-class/:classId', parents: FINANCIAL_DRILLDOWN, label: 'Fiche élève' }],
  },
  { path: '/student-duplicates', parents: [link(NAV.students)], label: "Doublons d'élèves" },
  { path: '/enrollments/:enrollmentId', parents: [link(NAV.enrollments)], label: "Demande d'inscription" },

  // Gestion pédagogique → Emploi du temps (CI) → Modifier l'emploi du temps
  // Emploi du temps (enseignant) → CI → Modifier l'emploi du temps
  {
    path: '/classes/:classId/timetable',
    parents: [link(NAV.classes)],
    label: 'Emploi du temps',
    formatLabel: (className) => `Emploi du temps (${className})`,
    alternatives: [{ from: NAV.teacherSchedule.to, parents: [link(NAV.teacherSchedule)], label: 'Classe' }],
  },
  // UX-86 — Gestion pédagogique → Enseignant principal (CI). L'alternative
  // n'est retenue que sur lien profond d'un enseignant (qui n'a pas accès à
  // /classes) : même repli que l'emploi du temps ci-dessus.
  {
    path: '/classes/:classId/teacher',
    parents: [link(NAV.classes)],
    label: 'Enseignant principal',
    formatLabel: (className) => `Enseignant principal (${className})`,
    alternatives: [
      {
        from: NAV.teacherSchedule.to,
        parents: [link(NAV.teacherSchedule)],
        label: 'Enseignant principal',
        formatLabel: (className) => `Enseignant principal (${className})`,
      },
    ],
  },

  // Comptabilité — drill-down DSH-04 (remplace le fil local de ces deux pages)
  { path: '/financial-by-class', parents: [link(NAV.financialDashboard)], label: NAV.financialByClass.label },
  { path: '/financial-by-class/:classId', parents: FINANCIAL_DRILLDOWN, label: 'Classe' },

  // Compte (hub UX-42)
  { path: '/profile', parents: [link(NAV.account)], label: 'Mes informations personnelles' },
  { path: '/change-password', parents: [link(NAV.account)], label: 'Changer mon mot de passe' },
];
